import { useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { X, ChevronDown, ChevronUp, Users, CheckSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/shared/StatusBadge';
import {
  useFrameworkReadiness,
  useFrameworkControls,
  useControlImplementation,
  useUpdateControlImpl,
} from '@/api/hooks/useCompliance';
import { useAuthStore } from '@/store/auth.store';
import { cn } from '@/lib/utils';
import type { Control } from '@/api/types/compliance.types';
import { AssignmentPanel } from '@/features/compliance/assignments/AssignmentPanel';
import { ScopeAssignModal } from '@/features/compliance/assignments/ScopeAssignModal';
import { BulkAssignModal } from '@/features/compliance/assignments/BulkAssignModal';
import { LEGACY_ASSIGN_MOVED } from '@/features/compliance/assignments/legacy-neutralized';
import { useScopeAssignments } from '@/features/compliance/assignments/scope-api';
import { useFrameworkAssignments } from '@/features/compliance/assignments/api';
import { AssigneeCell } from '@/components/compliance/AssigneeCell';
import { EvidencePanel } from '@/components/compliance/EvidencePanel';
import type { AssignmentState, ScopeAssignment, Assignment, InheritanceTier } from '@grc/shared';
import { STATUS_LABELS } from '@/features/compliance/statusLabels';

// Unified assignment status values for filter dropdown
const ASSIGNMENT_STATUSES: AssignmentState[] = [
  'unassigned', 'assigned', 'in_progress', 'submitted',
  'sent_back', 'implemented', 'compensating_control', 'not_applicable',
];

type SortOption = 'default' | 'status' | 'alpha' | 'completion';
const STATUS_ORDER: Record<string, number> = {
  implemented: 0, compensating_control: 1, not_applicable: 2,
  submitted: 3, sent_back: 4, in_progress: 5, assigned: 6, unassigned: 7,
};

function normaliseControlId(id: string): string {
  return id.replace(/\.\s+/g, '.');
}

function sortControls(controls: Control[], sort: SortOption, l3Map?: Map<string, Assignment>): Control[] {
  if (sort === 'default') return controls;
  return [...controls].sort((a, b) => {
    if (sort === 'status') {
      const aStatus = l3Map?.get(a.id)?.status ?? 'unassigned';
      const bStatus = l3Map?.get(b.id)?.status ?? 'unassigned';
      const ao = STATUS_ORDER[aStatus] ?? 99;
      const bo = STATUS_ORDER[bStatus] ?? 99;
      return ao !== bo ? ao - bo : a.control_id.localeCompare(b.control_id);
    }
    if (sort === 'alpha') return a.title.localeCompare(b.title);
    if (sort === 'completion') return (b.impl_evidence_count ?? 0) - (a.impl_evidence_count ?? 0);
    return 0;
  });
}

// ── Inheritance badge ─────────────────────────────────────────────────────────

const TIER_BADGE: Record<InheritanceTier, { label: string; variant: 'emerald' | 'sky' | 'violet' | 'muted' }> = {
  control:   { label: 'Explicit',              variant: 'emerald' },
  category:  { label: 'Inherited: Category',   variant: 'sky' },
  framework: { label: 'Inherited: Framework',  variant: 'violet' },
  none:      { label: 'Unassigned',            variant: 'muted' },
};

function InheritanceBadge({ tier, assignee }: { tier: InheritanceTier; assignee?: string | null }) {
  const { label, variant } = TIER_BADGE[tier];
  return (
    <Badge variant={variant} className="text-[9px] whitespace-nowrap" title={assignee ?? undefined}>
      {label}
    </Badge>
  );
}

// ── Compute effective tier per control (client-side) ─────────────────────────

function computeTier(
  control: Control,
  l3Map: Map<string, Assignment>,
  scopeRows: ScopeAssignment[],
): { tier: InheritanceTier; assigneeName: string | null } {
  // L3
  const l3 = l3Map.get(control.id);
  if (l3) {
    return { tier: 'control', assigneeName: l3.assignee_display_name ?? l3.assignee_email ?? null };
  }
  // L2
  const l2 = control.category
    ? scopeRows.find((s) => s.category === control.category && s.is_active)
    : null;
  if (l2) {
    return { tier: 'category', assigneeName: l2.assignee_display_name ?? l2.assignee_email ?? null };
  }
  // L1
  const l1 = scopeRows.find((s) => s.category === null && s.is_active);
  if (l1) {
    return { tier: 'framework', assigneeName: l1.assignee_display_name ?? l1.assignee_email ?? null };
  }
  return { tier: 'none', assigneeName: null };
}

// ── Control side panel ────────────────────────────────────────────────────────

function ControlPanel({
  control,
  onClose,
  effectiveTier,
  scopeAssignment,
  frameworkId,
}: {
  control: Control;
  onClose: () => void;
  effectiveTier: InheritanceTier;
  scopeAssignment: ScopeAssignment | null;
  frameworkId: string;
}) {
  const { hasAnyRole } = useAuthStore();
  const canManage = hasAnyRole(['risk_manager', 'admin', 'super_admin']);
  const { data: impl } = useControlImplementation(control.id);
  const updateMutation = useUpdateControlImpl(control.id);

  const [notes, setNotes] = useState(impl?.implementation_notes ?? '');
  const [targetDate, setTargetDate] = useState(impl?.target_date ?? '');
  const [guidanceOpen, setGuidanceOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);

  useState(() => {
    if (impl) {
      setNotes(impl.implementation_notes ?? '');
      setTargetDate(impl.target_date ?? '');
    }
  });

  function handleSave() {
    updateMutation.mutate(
      { implementationNotes: notes, targetDate: targetDate || null },
      {
        onSuccess: () => toast.success('Saved'),
        onError: () => toast.error('Failed to save'),
      },
    );
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="flex items-start justify-between gap-3 p-4 border-b border-border-subtle sticky top-0 bg-bg-surface z-10">
        <div>
          <p className="font-mono text-xs text-accent-cyan">{normaliseControlId(control.control_id)}</p>
          <p className="font-medium text-text-primary mt-0.5">{control.title}</p>
          <div className="mt-1">
            <InheritanceBadge
              tier={effectiveTier}
              assignee={scopeAssignment?.assignee_display_name ?? scopeAssignment?.assignee_email}
            />
          </div>
        </div>
        <button onClick={onClose} className="text-text-secondary hover:text-text-primary mt-0.5">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="p-4 space-y-4 flex-1">
        <p className="text-sm text-text-secondary">{control.description}</p>

        {control.implementation_guidance && (
          <div className="rounded-md border border-border-subtle overflow-hidden">
            <button onClick={() => setGuidanceOpen(!guidanceOpen)}
              className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-text-primary bg-bg-surface hover:bg-bg-surface-2">
              Implementation Guidance
              {guidanceOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {guidanceOpen && <div className="px-3 py-3 text-xs text-text-secondary whitespace-pre-wrap bg-bg-surface-2">{control.implementation_guidance}</div>}
          </div>
        )}
        {control.test_procedures && (
          <div className="rounded-md border border-border-subtle overflow-hidden">
            <button onClick={() => setTestOpen(!testOpen)}
              className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-text-primary bg-bg-surface hover:bg-bg-surface-2">
              Test Procedures
              {testOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {testOpen && <div className="px-3 py-3 text-xs text-text-secondary whitespace-pre-wrap bg-bg-surface-2">{control.test_procedures}</div>}
          </div>
        )}

        <div className="space-y-3 rounded-lg border border-border-subtle bg-bg-surface-2 p-3">
          <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Implementation Notes</p>
          <div className="space-y-1.5">
            <Label className="text-xs">Implementation notes</Label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!canManage} rows={3}
              className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary focus:outline-none resize-none disabled:opacity-50"
              placeholder="How is this control implemented?" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Target date</Label>
            <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} disabled={!canManage}
              className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-1.5 text-sm text-text-primary focus:outline-none disabled:opacity-50" />
          </div>
          {canManage && <Button size="sm" className="w-full" onClick={handleSave} disabled={updateMutation.isPending}>Save Changes</Button>}
        </div>

        <EvidencePanel controlId={control.id} layout="side-panel" />

        {/* Assignments — with scope/inherited context */}
        <AssignmentPanel
          controlId={control.id}
          frameworkId={frameworkId}
          effectiveTier={effectiveTier}
          scopeAssignment={scopeAssignment}
        />
      </div>
    </div>
  );
}

function getAssigneeDetails(
  control: Control,
  l3Map: Map<string, Assignment>,
  scopeRows: ScopeAssignment[],
  tier: InheritanceTier,
): { displayName: string | null; email: string | null; isPending: boolean } {
  const l3 = l3Map.get(control.id);
  if (l3) return { displayName: l3.assignee_display_name ?? null, email: l3.assignee_email ?? null, isPending: l3.assignee_is_pending ?? false };
  const scopeRow = tier === 'category'
    ? (scopeRows.find((s) => s.category === control.category && s.is_active) ?? null)
    : tier === 'framework'
      ? (scopeRows.find((s) => s.category === null && s.is_active) ?? null)
      : null;
  if (scopeRow) return { displayName: scopeRow.assignee_display_name ?? null, email: scopeRow.assignee_email ?? null, isPending: scopeRow.assignee_is_pending ?? false };
  return { displayName: null, email: null, isPending: false };
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function ControlTrackerPage() {
  const { frameworkId } = useParams<{ frameworkId: string }>();
  const navigate = useNavigate();
  const { hasAnyRole } = useAuthStore();
  const isCiso = hasAnyRole(['risk_manager', 'super_admin']);

  const [searchParams] = useSearchParams();
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') ?? '');
  const [filterCategory, setFilterCategory] = useState('');
  const [sort, setSort] = useState<SortOption>('default');
  const [selectedControl, setSelectedControl] = useState<Control | null>(null);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkModalOpen, setBulkModalOpen] = useState(false);

  // Scope assign modals
  const [frameworkScopeModal, setFrameworkScopeModal] = useState(false);
  const [categoryScopeModal, setCategoryScopeModal] = useState<string | null>(null); // category string

  const { data: readinessData } = useFrameworkReadiness(frameworkId!);
  // Load all controls (high limit avoids pagination complexity with grouped view)
  const { data, isLoading } = useFrameworkControls(
    frameworkId!,
    { status: filterStatus || undefined, category: filterCategory || undefined },
    0,
    500,
  );

  // Scope assignment data (L1 + L2)
  const { data: scopeRows = [] } = useScopeAssignments(frameworkId!);
  // L3 assignment data for the whole framework (for badge computation)
  const { data: l3Assignments = [] } = useFrameworkAssignments(frameworkId!);

  const l1ScopeRow = scopeRows.find((s) => s.category === null) ?? null;

  // Build control_id → Assignment map for L3 lookups (most-recent active per control)
  const l3Map = useMemo<Map<string, Assignment>>(() => {
    const m = new Map<string, Assignment>();
    for (const a of l3Assignments) {
      if (!m.has(a.control_id)) m.set(a.control_id, a);
    }
    return m;
  }, [l3Assignments]);

  const allControls = sortControls(data?.data ?? [], sort, l3Map);
  const readiness = readinessData?.readiness;
  const categories = Object.keys(readinessData?.controls_by_category ?? {});

  // CSF 2.0: detect function-aware frameworks
  const hasFunctions = useMemo(() => allControls.some((c) => c.function !== null), [allControls]);

  // CSF canonical function order
  const FUNCTION_ORDER = ['Govern', 'Identify', 'Protect', 'Detect', 'Respond', 'Recover'];

  // Two-level grouping for CSF: function → category → controls
  const groupedByFunction = useMemo<Map<string, Map<string, Control[]>>>(() => {
    const outer = new Map<string, Map<string, Control[]>>();
    if (!hasFunctions) return outer;
    const NO_CAT = '__none__';
    for (const ctrl of allControls) {
      const fn = ctrl.function ?? '__none__';
      const cat = ctrl.category ?? NO_CAT;
      if (!outer.has(fn)) outer.set(fn, new Map());
      const inner = outer.get(fn)!;
      if (!inner.has(cat)) inner.set(cat, []);
      inner.get(cat)!.push(ctrl);
    }
    return outer;
  }, [allControls, hasFunctions]);

  // Single-level grouping (non-CSF frameworks)
  const grouped = useMemo(() => {
    if (hasFunctions) return new Map<string, Control[]>();
    const map = new Map<string, Control[]>();
    const NO_CAT = '__none__';
    for (const ctrl of allControls) {
      const key = ctrl.category ?? NO_CAT;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(ctrl);
    }
    return map;
  }, [allControls, hasFunctions]);

  // When a category filter is active: show only that group (other groups hidden).
  // When no filter: show all groups.
  const visibleGroups = useMemo(() => {
    if (filterCategory) {
      const key = filterCategory;
      return map_of([[key, grouped.get(key) ?? []]]);
    }
    return grouped;
  }, [grouped, filterCategory]);

  function map_of<K, V>(entries: [K, V][]): Map<K, V> {
    return new Map(entries);
  }

  // Checkbox helpers
  function toggleControl(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleGroup(controls: Control[]) {
    const allSelected = controls.every((c) => selectedIds.has(c.id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) { controls.forEach((c) => next.delete(c.id)); }
      else { controls.forEach((c) => next.add(c.id)); }
      return next;
    });
  }

  function toggleAll() {
    const allCtrl = allControls;
    const allSelected = allCtrl.every((c) => selectedIds.has(c.id));
    setSelectedIds(allSelected ? new Set() : new Set(allCtrl.map((c) => c.id)));
  }

  const selectedCount = selectedIds.size;

  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <nav className="flex items-center gap-1.5 text-sm text-text-secondary">
          <button onClick={() => navigate('/compliance/workspace/frameworks')} className="hover:text-text-primary">Compliance</button>
          <span>/</span>
          <span className="text-text-primary font-medium">
            {readiness?.framework_display_name ?? readiness?.framework_name ?? 'Framework'}
          </span>
          <span>/</span>
          <span className="text-text-secondary">Controls</span>
        </nav>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-text-primary">
              {readiness?.framework_display_name ?? readiness?.framework_name ?? 'Control Tracker'}
            </h1>
            {readiness && (
              <p className="text-sm text-text-secondary mt-0.5">
                {Math.round(parseFloat(String(readiness.readiness_pct ?? 0)))}% ready ·{' '}
                Evidence coverage: {Math.round(parseFloat(String(readiness.evidence_coverage_pct ?? 0)))}%
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Framework-level scope assign */}
            {isCiso && (
              <Button
                variant="outline"
                size="sm"
                className="text-xs gap-1"
                disabled title={LEGACY_ASSIGN_MOVED}
                onClick={() => setFrameworkScopeModal(true)}
              >
                <Users className="h-3.5 w-3.5" />
                {l1ScopeRow ? 'Reassign Framework' : 'Assign Framework'}
              </Button>
            )}
            <span className="text-sm text-text-secondary">{allControls.length} controls</span>
          </div>
        </div>
      </div>

      {/* Filters + sort */}
      <div className="flex flex-wrap gap-3">
        <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); }}
          className="rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-1.5 text-sm text-text-primary focus:outline-none">
          <option value="">All statuses</option>
          {ASSIGNMENT_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
        {categories.length > 0 && (
          <select value={filterCategory} onChange={(e) => { setFilterCategory(e.target.value); }}
            className="rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-1.5 text-sm text-text-primary focus:outline-none">
            <option value="">All categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <select value={sort} onChange={(e) => setSort(e.target.value as SortOption)}
          className="rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-1.5 text-sm text-text-primary focus:outline-none ml-auto">
          <option value="default">Sort: Default</option>
          <option value="status">Sort: Status</option>
          <option value="alpha">Sort: A–Z</option>
          <option value="completion">Sort: Evidence (High–Low)</option>
        </select>
      </div>

      <div className={cn('flex gap-6', selectedControl && 'lg:pr-[400px]')}>
        <div className="flex-1 min-w-0">
          {isLoading ? (
            <div className="space-y-2">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border-subtle">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-subtle bg-bg-surface">
                    {isCiso && (
                      <th className="w-8 px-3 py-3">
                        <input
                          type="checkbox"
                          checked={allControls.length > 0 && allControls.every((c) => selectedIds.has(c.id))}
                          onChange={toggleAll}
                          className="rounded border-border-subtle"
                          title="Select all"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">Control ID</th>
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">Title</th>
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">Assignment</th>
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">Assignee</th>
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">Evidence</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle">
                  {hasFunctions
                    ? /* ── Two-level CSF rendering: Function → Category → Controls ── */
                      FUNCTION_ORDER.flatMap((fnName) => {
                        const catMap = groupedByFunction.get(fnName);
                        if (!catMap) return [];
                        // Apply category filter within functions
                        const filteredCatMap = filterCategory
                          ? new Map([[filterCategory, catMap.get(filterCategory) ?? []]])
                          : catMap;
                        const fnControls = Array.from(filteredCatMap.values()).flat();
                        if (fnControls.length === 0) return [];

                        return [
                          /* Function header row */
                          <tr key={`fn-${fnName}`} className="bg-[#0c1220] border-t-2 border-accent-cyan/30">
                            {isCiso && <td className="w-8 px-3 py-2" />}
                            <td colSpan={6} className="px-4 py-2">
                              <span className="text-xs font-bold text-accent-cyan uppercase tracking-widest">
                                {fnName}
                              </span>
                              <span className="text-[10px] text-text-secondary ml-2">({fnControls.length})</span>
                            </td>
                          </tr>,
                          /* Categories within this function */
                          ...Array.from(filteredCatMap.entries()).flatMap(([catKey, controls]) => {
                            if (controls.length === 0) return [];
                            const categoryName = catKey === '__none__' ? null : catKey;
                            const l2ScopeRow = categoryName
                              ? scopeRows.find((s) => s.category === categoryName && s.is_active) ?? null
                              : null;
                            const groupAllSelected = controls.every((c) => selectedIds.has(c.id));

                            return [
                              /* Category header row */
                              <tr key={`cat-${fnName}-${catKey}`} className="bg-bg-surface-2 border-t border-border-subtle">
                                {isCiso && (
                                  <td className="w-8 px-3 py-2">
                                    <input
                                      type="checkbox"
                                      checked={groupAllSelected}
                                      onChange={() => toggleGroup(controls)}
                                      className="rounded border-border-subtle"
                                      title={`Select all in ${categoryName ?? 'uncategorised'}`}
                                    />
                                  </td>
                                )}
                                <td colSpan={5} className="px-4 py-2 pl-6">
                                  <div className="flex items-center gap-3">
                                    <span className="text-xs font-semibold text-text-primary tracking-wide">
                                      {categoryName ?? 'Uncategorised'}
                                    </span>
                                    <span className="text-[10px] text-text-secondary">({controls.length})</span>
                                    {l2ScopeRow && (
                                      <span className="text-[10px] text-text-secondary">
                                        → {l2ScopeRow.assignee_display_name ?? l2ScopeRow.assignee_email}
                                      </span>
                                    )}
                                    {isCiso && categoryName && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-[10px] h-5 px-2 ml-auto"
                                        disabled title={LEGACY_ASSIGN_MOVED}
                                        onClick={() => setCategoryScopeModal(categoryName)}
                                      >
                                        <Users className="h-3 w-3 mr-1" />
                                        {l2ScopeRow ? 'Reassign Category' : 'Assign Category'}
                                      </Button>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-2" />
                              </tr>,
                              /* Control rows */
                              ...controls.map((ctrl) => {
                                const { tier, assigneeName } = computeTier(ctrl, l3Map, scopeRows);
                                const assigneeDetails = getAssigneeDetails(ctrl, l3Map, scopeRows, tier);
                                return (
                                  <tr
                                    key={ctrl.id}
                                    onClick={() => setSelectedControl(selectedControl?.id === ctrl.id ? null : ctrl)}
                                    className={cn(
                                      'cursor-pointer bg-bg-surface hover:bg-bg-surface-2 transition-colors',
                                      selectedControl?.id === ctrl.id && 'bg-accent-cyan/5',
                                    )}
                                  >
                                    {isCiso && (
                                      <td className="w-8 px-3 py-3" onClick={(e) => e.stopPropagation()}>
                                        <input
                                          type="checkbox"
                                          checked={selectedIds.has(ctrl.id)}
                                          onChange={() => toggleControl(ctrl.id)}
                                          className="rounded border-border-subtle"
                                        />
                                      </td>
                                    )}
                                    <td className="px-4 py-3 font-mono text-xs text-accent-cyan">
                                      {normaliseControlId(ctrl.control_id)}
                                    </td>
                                    <td className="px-4 py-3">
                                      <p className="font-medium text-text-primary line-clamp-1">{ctrl.title}</p>
                                    </td>
                                    <td className="px-4 py-3">
                                      <InheritanceBadge tier={tier} assignee={assigneeName} />
                                    </td>
                                    <td className="px-4 py-3">
                                      <AssigneeCell
                                        displayName={assigneeDetails.displayName}
                                        email={assigneeDetails.email}
                                        isPending={assigneeDetails.isPending}
                                      />
                                    </td>
                                    <td className="px-4 py-3">
                                      {(() => {
                                        const asgn = l3Map.get(ctrl.id);
                                        const st = asgn?.status ?? 'unassigned';
                                        return <StatusBadge status={st} label={STATUS_LABELS[st as AssignmentState] ?? st} />;
                                      })()}
                                    </td>
                                    <td className="px-4 py-3">
                                      {(ctrl.impl_evidence_count ?? 0) > 0 ? (
                                        <Badge variant="emerald" className="font-mono text-[10px]">{ctrl.impl_evidence_count}</Badge>
                                      ) : (
                                        <span className="text-text-secondary text-xs">—</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              }),
                            ];
                          }),
                        ];
                      })
                    : /* ── Single-level rendering (all non-CSF frameworks, unchanged) ── */
                      Array.from(visibleGroups.entries()).map(([catKey, controls]) => {
                        if (controls.length === 0) return null;
                        const categoryName = catKey === '__none__' ? null : (catKey as string);
                        const l2ScopeRow = categoryName
                          ? scopeRows.find((s) => s.category === categoryName && s.is_active) ?? null
                          : null;
                        const groupAllSelected = controls.every((c) => selectedIds.has(c.id));

                        return (
                          <>
                            {/* Category header row */}
                            <tr key={`cat-${catKey}`} className="bg-bg-surface-2 border-t-2 border-border-subtle">
                              {isCiso && (
                                <td className="w-8 px-3 py-2">
                                  <input
                                    type="checkbox"
                                    checked={groupAllSelected}
                                    onChange={() => toggleGroup(controls)}
                                    className="rounded border-border-subtle"
                                    title={`Select all in ${categoryName ?? 'uncategorised'}`}
                                  />
                                </td>
                              )}
                              <td colSpan={5} className="px-4 py-2">
                                <div className="flex items-center gap-3">
                                  <span className="text-xs font-semibold text-text-primary uppercase tracking-wide">
                                    {categoryName ?? 'Uncategorised'}
                                  </span>
                                  <span className="text-[10px] text-text-secondary">({controls.length})</span>
                                  {l2ScopeRow && (
                                    <span className="text-[10px] text-text-secondary">
                                      → {l2ScopeRow.assignee_display_name ?? l2ScopeRow.assignee_email}
                                    </span>
                                  )}
                                  {isCiso && categoryName && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-[10px] h-5 px-2 ml-auto"
                                      disabled title={LEGACY_ASSIGN_MOVED}
                                        onClick={() => setCategoryScopeModal(categoryName)}
                                    >
                                      <Users className="h-3 w-3 mr-1" />
                                      {l2ScopeRow ? 'Reassign Category' : 'Assign Category'}
                                    </Button>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-2" />
                            </tr>

                            {/* Control rows */}
                            {controls.map((ctrl) => {
                              const { tier, assigneeName } = computeTier(ctrl, l3Map, scopeRows);
                              const assigneeDetails = getAssigneeDetails(ctrl, l3Map, scopeRows, tier);
                              return (
                                <tr
                                  key={ctrl.id}
                                  onClick={() => setSelectedControl(selectedControl?.id === ctrl.id ? null : ctrl)}
                                  className={cn(
                                    'cursor-pointer bg-bg-surface hover:bg-bg-surface-2 transition-colors',
                                    selectedControl?.id === ctrl.id && 'bg-accent-cyan/5',
                                  )}
                                >
                                  {isCiso && (
                                    <td className="w-8 px-3 py-3" onClick={(e) => e.stopPropagation()}>
                                      <input
                                        type="checkbox"
                                        checked={selectedIds.has(ctrl.id)}
                                        onChange={() => toggleControl(ctrl.id)}
                                        className="rounded border-border-subtle"
                                      />
                                    </td>
                                  )}
                                  <td className="px-4 py-3 font-mono text-xs text-accent-cyan">
                                    {normaliseControlId(ctrl.control_id)}
                                  </td>
                                  <td className="px-4 py-3">
                                    <p className="font-medium text-text-primary line-clamp-1">{ctrl.title}</p>
                                  </td>
                                  <td className="px-4 py-3">
                                    <InheritanceBadge tier={tier} assignee={assigneeName} />
                                  </td>
                                  <td className="px-4 py-3">
                                    <AssigneeCell
                                      displayName={assigneeDetails.displayName}
                                      email={assigneeDetails.email}
                                      isPending={assigneeDetails.isPending}
                                    />
                                  </td>
                                  <td className="px-4 py-3">
                                    <StatusBadge
                                      status={ctrl.impl_status ?? 'not_started'}
                                      label={(ctrl.impl_status ?? 'not_started').replace(/_/g, ' ')}
                                    />
                                  </td>
                                  <td className="px-4 py-3">
                                    {(ctrl.impl_evidence_count ?? 0) > 0 ? (
                                      <Badge variant="emerald" className="font-mono text-[10px]">{ctrl.impl_evidence_count}</Badge>
                                    ) : (
                                      <span className="text-text-secondary text-xs">—</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </>
                        );
                      })
                  }
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Side panel */}
      {selectedControl && (() => {
        const { tier, assigneeName: _an } = computeTier(selectedControl, l3Map, scopeRows);
        const scopeAsgn = tier === 'category'
          ? (scopeRows.find((s) => s.category === selectedControl.category && s.is_active) ?? null)
          : tier === 'framework'
            ? (scopeRows.find((s) => s.category === null && s.is_active) ?? null)
            : null;
        return (
          <div className="fixed right-0 top-0 bottom-0 w-[400px] bg-bg-surface border-l border-border-subtle z-20 shadow-xl overflow-hidden flex flex-col">
            <ControlPanel
              control={selectedControl}
              onClose={() => setSelectedControl(null)}
              effectiveTier={tier}
              scopeAssignment={scopeAsgn}
              frameworkId={frameworkId!}
            />
          </div>
        );
      })()}

      {/* Sticky bulk-action bar */}
      {isCiso && selectedCount > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 rounded-full border border-border-subtle bg-bg-surface px-5 py-3 shadow-xl">
          <CheckSquare className="h-4 w-4 text-accent-cyan" />
          <span className="text-sm font-medium text-text-primary">{selectedCount} selected</span>
          <Button
            size="sm" className="text-xs"
            disabled title={LEGACY_ASSIGN_MOVED}
            onClick={() => setBulkModalOpen(true)}
          >
            Assign selected ({selectedCount})
          </Button>
          <button onClick={() => setSelectedIds(new Set())} className="text-xs text-text-secondary hover:text-text-primary">
            Clear
          </button>
        </div>
      )}

      {/* Framework scope modal */}
      {frameworkScopeModal && (
        <ScopeAssignModal
          frameworkId={frameworkId!}
          scopeType="framework"
          existing={l1ScopeRow ?? undefined}
          overrideCount={l3Assignments.length}
          onClose={() => setFrameworkScopeModal(false)}
        />
      )}

      {/* Category scope modal */}
      {categoryScopeModal && (
        <ScopeAssignModal
          frameworkId={frameworkId!}
          scopeType="category"
          category={categoryScopeModal}
          existing={scopeRows.find((s) => s.category === categoryScopeModal && s.is_active)}
          overrideCount={l3Map ? Array.from(l3Map.values()).filter((a) => {
            const ctrl = allControls.find((c) => c.id === a.control_id);
            return ctrl?.category === categoryScopeModal;
          }).length : 0}
          onClose={() => setCategoryScopeModal(null)}
        />
      )}

      {/* Bulk assign modal */}
      {bulkModalOpen && (
        <BulkAssignModal
          controlIds={Array.from(selectedIds)}
          onClose={() => setBulkModalOpen(false)}
          onSuccess={() => setSelectedIds(new Set())}
        />
      )}
    </div>
  );
}
